import { useEffect } from "react";

const DOMAIN = "https://yuccaflower.com";
const STORAGE_KEY = "yucca_lang";

/**
 * Detect user's preferred language from navigator.
 * Priority:
 *   1. Direct match — any browser language matches `allowed` (e.g. ru → ru, tr → tr).
 *   2. International fallback — if user has any non-Russian language and EN is allowed,
 *      return EN (covers de/fr/it/es/pt/... visitors who don't read Russian).
 *   3. null — caller falls back to default "ru".
 */
export function detectBrowserLang(allowed = ["ru", "en"]) {
  if (typeof navigator === "undefined") return null;
  const langs = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language || navigator.userLanguage || ""];

  for (const l of langs) {
    if (!l) continue;
    const code = String(l).toLowerCase().split("-")[0];
    if (allowed.includes(code)) return code;
  }

  // International fallback: non-Russian browser → EN
  const hasNonRussian = langs.some(l => l && !String(l).toLowerCase().startsWith("ru"));
  if (hasNonRussian && allowed.includes("en")) return "en";

  return null;
}

/**
 * Resolve language with priority:
 *   1. URL ?lang= param (explicit / shareable)
 *   2. localStorage saved user choice (sticky across visits)
 *   3. "ru" default (x-default)
 *
 * NOTE: navigator/browser auto-detection is intentionally NOT used for the
 * initial language. Otherwise a crawler (Googlebot sends Accept-Language: en)
 * or a first-time international visitor would render the clean /city URL in
 * English, while hreflang declares that URL as the ru / x-default version —
 * a content/hreflang mismatch that hurts indexing of the Russian pages.
 * International users can switch language via the selector (choice is persisted).
 * This also aligns with Google's guidance against automatic language redirection.
 */
export function getLangFromUrl(searchParams, availableLanguages = ["ru", "en"]) {
  const urlLang = searchParams?.get?.("lang");
  if (urlLang && availableLanguages.includes(urlLang)) return urlLang;
  const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  if (saved && availableLanguages.includes(saved)) return saved;
  return "ru";
}

/**
 * Same resolution logic but reads from window.location directly.
 * Use in components that do NOT use react-router's useSearchParams (e.g. weddings, legal).
 */
export function getLangFromWindow(availableLanguages = ["ru", "en"]) {
  if (typeof window === "undefined") return "ru";
  const params = new URLSearchParams(window.location.search);
  return getLangFromUrl(params, availableLanguages);
}

/**
 * Update URL ?lang= parameter without full reload + persist to localStorage.
 */
export function setLangInUrl(lang, searchParams, setSearchParams) {
  const newParams = new URLSearchParams(searchParams);
  if (lang === "ru") {
    newParams.delete("lang");
  } else {
    newParams.set("lang", lang);
  }
  setSearchParams(newParams, { replace: true });
  if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, lang);
}

/**
 * Same as setLangInUrl but uses window.history.replaceState — for components
 * without react-router's setSearchParams.
 */
export function setLangInWindowUrl(lang) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (lang === "ru") url.searchParams.delete("lang");
  else url.searchParams.set("lang", lang);
  window.history.replaceState({}, "", url);
  localStorage.setItem(STORAGE_KEY, lang);
}

/**
 * Hook (DISABLED for SEO): previously synced auto-detected language into the URL
 * via replaceState. That polluted the clean /city URL with ?lang=en for crawlers
 * (Googlebot sends Accept-Language: en), which fragmented canonical/hreflang
 * signals — the clean RU/x-default URL ended up canonicalizing to its ?lang=en
 * variant. Auto-detection now only affects the visible display language; the URL
 * is changed ONLY on explicit user selection (setLangInUrl). Kept as a no-op so
 * existing call sites stay unchanged.
 */
export function useAutoSyncLangToUrl() {
  // Intentionally a no-op. See doc comment above.
}

/**
 * Inject hreflang link tags into <head> for SEO.
 * Google uses these to serve the right language version to the right audience.
 */
export function useHreflang(path, availableLanguages = ["ru", "en"]) {
  useEffect(() => {
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

    availableLanguages.forEach(lng => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lng;
      link.href = lng === "ru" ? `${DOMAIN}${path}` : `${DOMAIN}${path}${path.includes("?") ? "&" : "?"}lang=${lng}`;
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    });

    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = `${DOMAIN}${path}`;
    xDefault.setAttribute("data-hreflang", "true");
    document.head.appendChild(xDefault);

    return () => {
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
    };
  }, [path, availableLanguages]);
}

/**
 * Update the canonical URL.
 *
 * Canonical must be SELF-REFERENTIAL and based ONLY on the explicit `?lang=`
 * param present in the current URL — never on the auto-detected display language.
 * Otherwise the clean `/city` URL (ru / x-default) would wrongly canonicalize to
 * its `?lang=en` variant whenever a crawler/visitor is auto-switched to English.
 *
 *   /antalya            -> https://yuccaflower.com/antalya
 *   /antalya?lang=en    -> https://yuccaflower.com/antalya?lang=en
 *
 * `availableLanguages` guard: a `?lang=` the page does NOT support (e.g. ?lang=tr
 * on a Spanish city — Turkish exists only for Turkey / Georgia / Azerbaijan) renders
 * RU content, so the canonical must point to the clean RU URL instead of
 * self-referencing the duplicate (mirrors backend `_tr_guard`).
 */
export function updateCanonical(path, availableLanguages = ["ru", "en"]) {
  const canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) return;
  const urlLang = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("lang")
    : null;
  const langs = Array.isArray(availableLanguages) ? availableLanguages : ["ru", "en"];
  const suffix = urlLang && urlLang !== "ru" && langs.includes(urlLang)
    ? `${path.includes("?") ? "&" : "?"}lang=${urlLang}`
    : "";
  canonical.setAttribute("href", `${DOMAIN}${path}${suffix}`);
}
